import {
  IconChart,
  IconBuilding,
  IconGlobe,
  IconBook,
} from "../GlobalResearch/icons";

/* =========================================================================== *
 *  CATEGORY METADATA
 *  Drives the hero copy, the tab bar, the nav dropdown and the per-stream
 *  accent. Keys double as route names in App.
 * =========================================================================== */
export const CATEGORIES = [
  {
    key: "all",
    label: "All Research",
    // short: "Full repository",
    eyebrow: "Research Repository",
    // title: "Empowering Decisions with Global Intelligence",
    sub: "A centralized knowledge hub providing rigorous, evidence-based economic, business, trade and socio-economic research for stakeholders worldwide.",
    icon: IconBook,
  },
  {
    key: "egd",
    label: "Economic Growth & Development",
    // short: "Macro & growth research",
    eyebrow: "Economic Growth & Development",
    // title: "Evidence on India's Economic Transformation",
    sub: "Analytical reports, working papers and thematic studies examining the Indian and global economic and policy landscape — and the reforms that strengthen competitiveness and inclusive growth.",
    icon: IconChart,
  },
  {
    key: "eodb",
    label: "Ease of Doing Business",
    // short: "Regulation & reform",
    eyebrow: "Ease of Doing Business (EoDB)",
    // title: "Reforms for a Business-Friendly India",
    sub: "Deregulation studies, policy ready reckoners and sector reports that decode regulatory frameworks, compliance burdens and market opportunities for enterprises of every size.",
    icon: IconBuilding,
  },
  {
    key: "tips",
    label: "Trade & Investment Promotion Services",
    // short: "Trade & investment",
    eyebrow: "Trade & Investment Promotion Services",
    // title: "Intelligence for Trade & Investment",
    sub: "Strategic insights into high-growth sectors, investment opportunities and the evolving policy and regulatory landscape — supporting informed decisions and cross-border collaboration.",
    icon: IconGlobe,
  },
  {
    key: "publications",
    label: "Publications",
    // short: "Flagship periodicals",
    eyebrow: "Flagship Publications",
    // title: "Timely Research & Economic Intelligence",
    sub: "Our flagship periodicals and briefs track macroeconomic trends, policy developments, sectoral performance and the global outlook — delivered daily, weekly and monthly.",
    icon: IconBook,
  },
];

export const CATEGORY_MAP = Object.fromEntries(
  CATEGORIES.map((c) => [c.key, c])
);

/* Chip styling per document type — kept inside the warm brand family so the
   repository never drifts into the reference image's cold navy palette. */
export const TYPE_STYLES = {
  REPORT: "bg-brand-red/10 text-brand-red ring-brand-red/20",
  OUTLOOK: "bg-[#FAF7F2] text-brand-orange-dark ring-brand-orange/25",
  "WHITEPAPER": "bg-brand-orange/10 text-brand-orange-dark ring-brand-orange/25",
  "POLICY BRIEF": "bg-amber-100 text-amber-700 ring-amber-300/70",
  "CASE STUDY": "bg-brand-red/5 text-brand-red-dark ring-brand-red/15",
  "WORKING PAPER": "bg-orange-50 text-orange-700 ring-orange-200",

  /* EoDB stream types */
  "EODB Event": "bg-amber-100 text-amber-700 ring-amber-300/70",
  "EODB Publications": "bg-brand-orange/10 text-brand-orange-dark ring-brand-orange/25",

  /* Publications stream types */
  "Daily ABCD": "bg-brand-red/10 text-brand-red ring-brand-red/20",
  "Weekly AIM": "bg-orange-50 text-orange-700 ring-orange-200",
  "Monthly GEO": "bg-[#FAF7F2] text-brand-orange-dark ring-brand-orange/25",
  "Research Note": "bg-brand-red/5 text-brand-red-dark ring-brand-red/15",
};

/* =========================================================================== *
 *  THE REPOSITORY — mock PDF documents distributed across the four streams.
 *  Every card's View / Download action synthesises a genuine one-page PDF
 *  in-browser (see buildPdf below), so the buttons truly work offline.
 * =========================================================================== */
export const DOCUMENTS = [
  /* ---------------- Economic Growth & Development ---------------- */
  {
    id: "egd-2025-outlook",
    title: "Annual India Economic Outlook 2025: Growth, Inflation & Reform Priorities",
    type: "REPORT",
    category: "egd",
    year: 2025,
    topic: "Macroeconomy",
    region: "India",
    author: "Dr. S.P. Sharma",
    date: "Jan 14, 2025",
    pages: 48,
    sizeKB: 1820,
    downloads: 4210,
    abstract:
      "A comprehensive analysis of India's growth trajectory, inflation dynamics and the structural reform agenda for 2025, with scenario-based projections for GDP, capex and the current account.",
  },
  {
    id: "egd-manufacturing-2024",
    title: "Manufacturing Competitiveness and the China-Plus-One Opportunity",
    type: "REPORT",
    category: "egd",
    year: 2024,
    topic: "Manufacturing",
    region: "Asia-Pacific",
    author: "Dr. S.P. Sharma",
    date: "Sep 02, 2024",
    pages: 36,
    sizeKB: 1490,
    downloads: 3120,
    abstract:
      "Maps India's position in shifting global value chains and quantifies the labour, logistics and policy levers that determine whether the China-plus-one dividend converts into durable factory investment.",
  },
  {
    id: "egd-inclusive-2024",
    title: "Inclusive Growth: Employment, Wages and Social Mobility",
    type: "WORKING PAPER",
    category: "egd",
    year: 2024,
    topic: "Employment",
    region: "India",
    author: "Global Research Team",
    date: "Jun 18, 2024",
    pages: 28,
    sizeKB: 1120,
    downloads: 1980,
    abstract:
      "Examines the quality of job creation, real-wage trends and the transmission of growth into household incomes, proposing a scoreboard for inclusive and labour-intensive development.",
  },
  {
    id: "egd-financial-2023",
    title: "Financial Stability and Credit Growth in a High-Rate Cycle",
    type: "WORKING PAPER",
    category: "egd",
    year: 2023,
    topic: "Finance",
    region: "G20",
    author: "Dr. S.P. Sharma",
    date: "Nov 09, 2023",
    pages: 32,
    sizeKB: 1340,
    downloads: 1540,
    abstract:
      "Assesses balance-sheet resilience across banks and NBFCs, the credit impulse to investment, and the financial-stability risks embedded in a prolonged higher-for-longer rate environment.",
  },

  /* ---------------- Ease of Doing Business ---------------- */
  {
    id: "eodb-regulation-2025",
    title: "State of Regulation 2025: Deregulation and the Compliance Burden",
    type: "EODB Publications",
    category: "eodb",
    year: 2025,
    topic: "Regulation",
    region: "India",
    author: "EoDB Policy Unit",
    date: "Feb 20, 2025",
    pages: 44,
    sizeKB: 1760,
    downloads: 3890,
    abstract:
      "A cross-sector audit of compliance costs and a prioritised deregulation roadmap — benchmarking Indian states on the time, cost and certainty of doing business.",
  },
  {
    id: "eodb-reckoner-2024",
    title: "Ready Reckoner: Starting a Business Across Indian States",
    type: "EODB Publications",
    category: "eodb",
    year: 2024,
    topic: "Regulation",
    region: "India",
    author: "EoDB Policy Unit",
    date: "Aug 12, 2024",
    pages: 22,
    sizeKB: 980,
    downloads: 2640,
    abstract:
      "A practitioner's guide to incorporation, licences and approvals, comparing state-level procedures, fees and timelines so entrepreneurs can navigate the regulatory maze with confidence.",
  },
  {
    id: "eodb-msme-2023",
    title: "MSME Access to Credit and Formalisation Pathways",
    type: "EODB Event",
    category: "eodb",
    year: 2023,
    topic: "Finance",
    region: "Emerging Markets",
    author: "Global Research Team",
    date: "May 30, 2023",
    pages: 26,
    sizeKB: 1080,
    downloads: 1420,
    abstract:
      "Field evidence on how credit guarantees, account-based lending and digital rails are reshaping finance for micro and small enterprises — and where the last mile still breaks down.",
  },
  {
    id: "eodb-insolvency-2022",
    title: "Insolvency and Contract Enforcement: A Reform Scorecard",
    type: "EODB Event",
    category: "eodb",
    year: 2022,
    topic: "Regulation",
    region: "G20",
    author: "EoDB Policy Unit",
    date: "Oct 05, 2022",
    pages: 30,
    sizeKB: 1210,
    downloads: 1180,
    abstract:
      "Scores resolution frameworks on recovery rates, timeliness and creditor rights, drawing cross-country lessons to sharpen India's insolvency and contract-enforcement regime.",
  },

  /* ---------------- Trade & Investment Promotion Services ---------------- */
  {
    id: "tips-fdi-2025",
    title: "Foreign Direct Investment Trends and Sectoral Hotspots 2025",
    type: "REPORT",
    category: "tips",
    year: 2025,
    topic: "Investment",
    region: "Global",
    author: "TIPS Advisory Desk",
    date: "Mar 11, 2025",
    pages: 40,
    sizeKB: 1640,
    downloads: 3560,
    abstract:
      "Tracks FDI flows into India, identifies the sectors absorbing fresh capital, and benchmarks investor sentiment against competing destinations across the Global South.",
  },
  {
    id: "tips-exports-2024",
    title: "Export Diversification: From Goods to a Services-Led Trade Surplus",
    type: "WHITEPAPER",
    category: "tips",
    year: 2024,
    topic: "Trade",
    region: "India",
    author: "TIPS Advisory Desk",
    date: "Jul 22, 2024",
    pages: 34,
    sizeKB: 1380,
    downloads: 2210,
    abstract:
      "Argues that India's next trade surplus lies in digital, professional and financial services, and sets out the market-access and skills agenda to capture it.",
  },
  {
    id: "tips-dataflows-2023",
    title: "Cross-Border Data Flows and Digital Trade Agreements",
    type: "POLICY BRIEF",
    category: "tips",
    year: 2023,
    topic: "Digital Economy",
    region: "Asia-Pacific",
    author: "Global Research Team",
    date: "Dec 01, 2023",
    pages: 18,
    sizeKB: 820,
    downloads: 1670,
    abstract:
      "Explains the regulatory frameworks required to keep data secure while enabling international digital trade, and positions India within emerging digital-economy agreements.",
  },
  {
    id: "tips-green-2024",
    title: "Green Investment and the Energy-Transition Capital Stack",
    type: "CASE STUDY",
    category: "tips",
    year: 2024,
    topic: "Sustainability",
    region: "Global",
    author: "TIPS Advisory Desk",
    date: "Apr 16, 2024",
    pages: 29,
    sizeKB: 1190,
    downloads: 1990,
    abstract:
      "Deconstructs how renewables, storage and green hydrogen get financed — blending concessional capital, blended finance and commercial debt into a workable transition stack.",
  },

  /* ---------------- Publications ---------------- */
  {
    id: "pub-abc-2025",
    title: "ABC Daily Digest — Markets, Policy and the Week Ahead",
    type: "Daily ABCD",
    category: "publications",
    year: 2025,
    topic: "Macroeconomy",
    region: "India",
    author: "Editorial Desk",
    date: "Mar 28, 2025",
    pages: 12,
    sizeKB: 540,
    downloads: 5120,
    abstract:
      "The ASSOCHAM Business Currents Daily (ABC Daily) in a single downloadable edition — the market moves, policy signals and data releases that shaped the week.",
  },
  {
    id: "pub-aim-2024",
    title: "ASSOCHAM India Monitor (AIM) — Q4 2024 Edition",
    type: "Weekly AIM",
    category: "publications",
    year: 2024,
    topic: "Macroeconomy",
    region: "India",
    author: "Editorial Desk",
    date: "Dec 20, 2024",
    pages: 24,
    sizeKB: 1020,
    downloads: 2880,
    abstract:
      "The weekly AIM monitor compiled for the quarter: high-frequency indicators, sectoral momentum and the policy calendar driving the Indian economy into 2025.",
  },
  {
    id: "pub-geo-2025",
    title: "ASSOCHAM Global Economic Outlook (GEO) — 2025",
    type: "Monthly GEO",
    category: "publications",
    year: 2025,
    topic: "Macroeconomy",
    region: "Global",
    author: "Dr. S.P. Sharma",
    date: "Jan 30, 2025",
    pages: 52,
    sizeKB: 2010,
    downloads: 4670,
    abstract:
      "The monthly GEO's annual flagship issue — a region-by-region read on growth, rates, trade and risk, and what the global cycle means for India's policymakers and firms.",
  },
  {
    id: "pub-infra-2023",
    title: "Sectoral Performance Tracker: Infrastructure & Logistics",
    type: "Research Note",
    category: "publications",
    year: 2023,
    topic: "Infrastructure",
    region: "India",
    author: "Global Research Team",
    date: "Sep 14, 2023",
    pages: 27,
    sizeKB: 1100,
    downloads: 1360,
    abstract:
      "A dashboard-style read on capex execution, freight corridors and logistics costs, tracking where infrastructure spend is translating into real productivity gains.",
  },
];

/* ----------------------------- option helpers ---------------------------- */
const uniqSorted = (arr, cmp) => [...new Set(arr)].sort(cmp);

export function optionsFor(catKey) {
  const pool =
    catKey === "all"
      ? DOCUMENTS
      : DOCUMENTS.filter((d) => d.category === catKey);
  const years = uniqSorted(pool.map((d) => d.year), (a, b) => b - a);
  const topics = uniqSorted(pool.map((d) => d.topic), (a, b) =>
    a.localeCompare(b)
  );
  const regions = uniqSorted(pool.map((d) => d.region), (a, b) =>
    a.localeCompare(b)
  );
  const types = uniqSorted(pool.map((d) => d.type), (a, b) =>
    a.localeCompare(b)
  );
  return { years, topics, regions, types };
}

export const formatDownloads = (n) =>
  n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, "") + "k" : String(n);

export const formatSize = (kb) =>
  kb >= 1024 ? (kb / 1024).toFixed(1) + " MB" : kb + " KB";

export const slugify = (s) =>
  String(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

/* =========================================================================== *
 *  Minimal, spec-correct one-page PDF generator.
 *  Byte offsets for the cross-reference table are computed with TextEncoder
 *  so the produced file opens cleanly in any viewer. Lets the repository's
 *  "View Document" / "Download" actions deliver a real PDF with no server.
 * =========================================================================== */
function escPdf(s) {
  return String(s).replace(/([\\()])/g, "\\$1");
}

export function buildPdf(doc) {
  const cat = CATEGORY_MAP[doc.category]?.label || doc.category;
  const lines = [
    "ASSOCHAM Global Research & Strategy Centre",
    "",
    "Stream:    " + cat,
    "Title:     " + doc.title,
    "Type:      " + doc.type,
    "Author:    " + doc.author,
    "Date:      " + doc.date,
    "Region:    " + doc.region + "   |   Topic: " + doc.topic,
    "",
    "Abstract:",
    ...wrap(doc.abstract, 78),
    "",
    "[ Placeholder research document generated for demonstration. ]",
  ];

  let stream = "BT /F1 12 Tf 17 TL 56 780 Td\n";
  stream += "0.70 0.13 0.09 rg (" + escPdf(lines[0]) + ") Tj 0 0 0 rg\n";
  for (let i = 1; i < lines.length; i++) {
    stream += "T* (" + escPdf(lines[i]) + ") Tj\n";
  }
  stream += "ET";

  const enc = new TextEncoder();
  const streamBytes = enc.encode(stream).length;

  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>",
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
    `<< /Length ${streamBytes} >>\nstream\n${stream}\nendstream`,
  ];

  const header = "%PDF-1.4\n";
  let pdf = header;
  const offsets = [];
  objects.forEach((body, i) => {
    offsets[i] = enc.encode(pdf).length;
    pdf += `${i + 1} 0 obj\n${body}\nendobj\n`;
  });
  const xrefStart = enc.encode(pdf).length;
  const size = objects.length + 1;
  pdf += `xref\n0 ${size}\n0000000000 65535 f \n`;
  offsets.forEach((off) => {
    pdf += String(off).padStart(10, "0") + " 00000 n \n";
  });
  pdf += `trailer\n<< /Size ${size} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

  return new Blob([pdf], { type: "application/pdf" });
}

function wrap(text, width) {
  const words = String(text).split(/\s+/);
  const out = [];
  let line = "";
  for (const w of words) {
    if ((line + " " + w).trim().length > width) {
      out.push(line.trim());
      line = w;
    } else {
      line = (line + " " + w).trim();
    }
  }
  if (line) out.push(line);
  return out;
}

export function openDoc(doc) {
  const url = URL.createObjectURL(buildPdf(doc));
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function downloadDoc(doc) {
  const url = URL.createObjectURL(buildPdf(doc));
  const a = document.createElement("a");
  a.href = url;
  a.download = slugify(doc.title) + ".pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}